import backgroundTint from "components/show-win-lines/background-tint";
import sgWildsComponent from "components/wilds/sg-wilds-component";
import {HighlightSymbolsCommand} from "slotworks/commands/symbols/highlight-symbols-command";
import {setContinuousAnimate} from "slotworks/commands/symbols/set-continuous-animate-command";
import {SetSymbolEffects} from "slotworks/commands/symbols/set-symbol-effects";
import Components from "slotworks/components/components";
import SymbolComponent from "slotworks/components/matrix/symbol/symbol-component";
import AutoplayModel from "slotworks/model/autoplay/autoplay-model";
import BetModel from "slotworks/model/bets/bet-model";
import gameState from "slotworks/model/game-state";
import Gameplay from "slotworks/model/gameplay/gameplay";
import FreespinRecord from "slotworks/model/gameplay/records/freespin-record";
import Record from "slotworks/model/gameplay/records/record";
import LineResult from "slotworks/model/gameplay/records/results/line-result";
import SymbolWinResult from "slotworks/model/gameplay/records/results/symbol-win-result";
import {Services} from "slotworks/services/services";
import ShowWinLines from "slotworks/state-machine/standard/states/show-win-lines";
import Contract from "slotworks/utils/contract";
import Parallel from "slotworks/utils/contracts/parallel";
import sgWinRespinCelebrator from "components/sg-win-respin-celebrator";

export default class SGShowWinLines extends ShowWinLines {

    public static AUTOPLAY_WIN_DELAY: number = 1000;
    protected contract: Contract<void>;

    public onEnter() {
        const wildSymbols = Components.matrix.getAllSymbols().filter((symbol) => symbol.symbolId.indexOf("expanded") > -1);
        setContinuousAnimate(wildSymbols, true);
        if (!this.bigWinPending) {
            this.playLineSound();
        }
        if (!this.isFreeSpin()) {
            Services.sound.customEvent("base-game-sound-decrease");
        }
        super.onEnter();
    }

    public onSkip() {
        Services.sound.customEvent("skip_win_lines");
        super.onSkip();
    }

    public onExit() {
        setContinuousAnimate(Components.matrix.getAllSymbols(), false);
        Components.matrix.resetSymbols();
        if (!this.isFreeSpin()) {
            Services.sound.customEvent("base-game-sound-normalise");
        }
        super.onExit();
    }

    private playLineSound(): void {
        const cashWon = gameState.getCurrentGame().getCurrentRecord().cashWon;
        const stake = BetModel.getTotalStake();
        if (cashWon < stake && cashWon !== 0) {
            Services.sound.customEvent("win_line_1");
        } else if (cashWon >= stake && cashWon < stake * 2) {
            Services.sound.customEvent("win_line_2");
        } else if (cashWon >= stake * 2 && cashWon < stake * 10) {
            Services.sound.customEvent("win_line_3");
        } else if (cashWon >= stake * 10) {
            Services.sound.customEvent("win_line_4");
        }
    }

    protected winValueCelebration(): Contract<void> {
        const contract: Contract<void> = super.winValueCelebration();

        const gameplay: Gameplay = gameState.getCurrentGame();
        if (gameplay.inFreespins() || AutoplayModel.isAutoPlaying()) {
            const record: Record = gameplay.getCurrentRecord();
            const totalWin: number = gameplay.getTotalWin();

            if (this.bigWinTriggered()) {
                Services.transaction.setWinnings(0, null, 0).execute();
            } else {
                const tickTime = AutoplayModel.isAutoPlaying() ? 0 : ShowWinLines.WIN_TICK_TIME;

                Services.transaction.setWinnings(record.cashWon, totalWin, tickTime).execute();
            }
        }

        return contract;
    }

    protected highlightWin(result: SymbolWinResult, color: number) {
        const record: Record = gameState.getCurrentGame().getCurrentRecord();

        backgroundTint.show();
        backgroundTint.updateDepth(record.results);

        const symbolsToAnimate = this.setupSymbolsToAnimate(result);

        // Show line
        if (result instanceof LineResult) {
            
            ShowWinLines.MIN_DISPLAY_TIME = AutoplayModel.isAutoPlaying() ? SGShowWinLines.AUTOPLAY_WIN_DELAY : 0;
            const reverse = result.positions[0].x !== 0;
            Components.lines.highlight(result.line, reverse);
        }

        // Tint and filter effects
        const filters: Array<PIXI.Filter<any>> = this.getFilterEffects(color);
        if (ShowWinLines.ENABLE_LINE_EFFECTS) {
            if (result instanceof LineResult) {
                if (filters.length) {
                    Components.lines.filter(result.line, filters);
                } else {
                    Components.lines.tint(result.line, color);
                }
            }
        }
        if (ShowWinLines.ENABLE_BEHAVIOUR_EFFECTS) {
            SetSymbolEffects(symbolsToAnimate, color, filters, ShowWinLines.APPLY_EFFECTS_TO_SYMBOL_BEHAVIOURS);
        }

        const highlightSymbols = HighlightSymbolsCommand(symbolsToAnimate.filter(item => item.symbolId.indexOf("expanded") === -1), true);

        return new Parallel([
            () => highlightSymbols,
            () => this.addWildHighlight(symbolsToAnimate)
        ]);

    }

    private addWildHighlight(symbolsToAnimate: SymbolComponent[]): Contract<void> {
        let isWildHighlight = false;
        const wildFlags: boolean[] = [false, false, false];

        symbolsToAnimate.forEach(item => {
            if (item.symbolId.indexOf("expanded") > -1) {
                wildFlags[item.gridPosition.x - 2] = true;
                isWildHighlight = true;
            }
        });
        if (isWildHighlight) {
            return  sgWildsComponent.playHighlightAnimation(wildFlags);
        } else {
            return Contract.empty();
        }
    }
    
    private isFreeSpin(): boolean {
        const gameplay: Gameplay = gameState.getCurrentGame();
        return gameplay.inFreespins();
    }
}
